import React, { useCallback, useMemo, useState } from "react";

import Modal from "../../../components/modal/Modal";
import SvgLoadingAnimation from "../../../components/loading";
import WithdrawalRequestModal from "../../../components/withdrawal-request-modal/WithdrawalRequestModal";
import {
  useCreateAccountPaymentRequestMutation,
  useGetAccountMeQuery,
  useGetAccountWithdrawableTransactionsQuery,
  type AccountUser,
} from "../../../app/services/accounts";
import { computeDisplayBalances } from "../../../utils/balance";
import { addConsumptionTax } from "../../../utils/tax";
import { startActAs } from "../../../utils/actAs";

// 振込申請の最小額（BE の MIN_AMOUNT と一致、税抜の settled で判定）
const MIN_WITHDRAWAL_AMOUNT = 100;

// 振込に必要な銀行口座フィールド（GET /api/me のレスポンス名）。
// AccountPopover と同じ判定。
const REQUIRED_BANK_FIELDS = [
  "bank",
  "bank_branch",
  "bank_branch_number",
  "account_type",
  "account_number",
  "account_holder",
] as const;

interface AccountWithdrawalModalProps {
  /** 一覧で「振込申請」を押した対象ユーザー */
  user: AccountUser;
  /** 対象ユーザーの表示名（act-as 開始・案内文で使用） */
  displayName: string;
  onClose: () => void;
}

/**
 * アカウント管理ページの「振込申請」モーダル。
 *
 * 対象ユーザー本人として（`X-Act-As-User` 付き）残高・振込対象取引を取得し、
 * 既存のグローバル `WithdrawalRequestModal` に注入する。申請ボタンも
 * act-as 付き POST で、本人が操作したときと同じように申請まで完了できる。
 *
 * AccountPopover と同じ前提チェックを行う:
 * - 振込可能残高（税抜 settled >= ¥100）が無ければ案内モーダル
 * - 銀行口座未登録なら登録案内モーダル（act-as でマイページを開ける）
 */
const AccountWithdrawalModal: React.FC<AccountWithdrawalModalProps> = ({
  user,
  displayName,
  onClose,
}) => {
  const { data: meResponse, isLoading: isMeLoading } = useGetAccountMeQuery(
    user.id,
  );
  const { data: withdrawableData, isLoading: isTxLoading } =
    useGetAccountWithdrawableTransactionsQuery(user.id);

  const [createPaymentRequest, { isLoading: isSubmitting }] =
    useCreateAccountPaymentRequestMutation();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meData = meResponse?.data ?? null;

  const balances = useMemo(
    () => (meData ? computeDisplayBalances(meData) : null),
    [meData],
  );
  const canWithdraw = balances
    ? balances.settledExclusive >= MIN_WITHDRAWAL_AMOUNT
    : false;
  const hasBankInfo = meData
    ? REQUIRED_BANK_FIELDS.every((field) => !!meData[field])
    : false;

  // 申請額は振込リストと同じレスポンスの settled_balance（税抜・申請原資）を
  // 税込換算して表示する（AccountPopover と同じ算出）。
  const withdrawSettledExclusive = Number(
    withdrawableData?.data?.settled_balance ?? 0,
  );
  const withdrawAmountLabel = `¥${addConsumptionTax(
    Number.isFinite(withdrawSettledExclusive) ? withdrawSettledExclusive : 0,
  ).toLocaleString()}`;

  const handleSubmit = useCallback(async () => {
    setError(null);
    try {
      // amount 省略 = 対象ユーザーの settled 全額を申請
      await createPaymentRequest({ userId: user.id }).unwrap();
      setDone(true);
    } catch {
      setError("振込申請に失敗しました。時間をおいて再度お試しください。");
    }
  }, [createPaymentRequest, user.id]);

  const handleOpenMyPageAsUser = useCallback(() => {
    startActAs({ userId: user.id, displayName }, "/my-page");
  }, [user.id, displayName]);

  if (isMeLoading || isTxLoading) {
    return <SvgLoadingAnimation />;
  }

  // 振込可能残高なし → 案内のみ
  if (!canWithdraw) {
    return (
      <Modal
        onClose={onClose}
        title="振込可能な残高がありません"
        description={`${displayName} さんには現在、振込申請できる残高（検収完了済みの受取金）がありません。`}
        primaryAction={{
          label: "閉じる",
          onClick: onClose,
          autoFocus: true,
        }}
      />
    );
  }

  // 銀行口座未登録 → 登録案内（act-as でマイページを開ける）
  if (!hasBankInfo) {
    return (
      <Modal
        onClose={onClose}
        title="振込先銀行口座の登録が必要です"
        description={`振込申請を行うには、${displayName} さんの振込先銀行口座の登録が必要です。本人として操作を開始し、マイページで口座情報を登録できます。`}
        primaryAction={{
          label: "本人としてマイページを開く",
          onClick: handleOpenMyPageAsUser,
        }}
        secondaryAction={{
          label: "キャンセル",
          onClick: onClose,
          theme: "secondary",
          autoFocus: true,
        }}
      />
    );
  }

  return (
    <WithdrawalRequestModal
      transactions={withdrawableData?.data?.transactions ?? []}
      currentUserId={user.id}
      amountLabel={withdrawAmountLabel}
      isSubmitting={isSubmitting}
      done={done}
      error={error}
      bankName={meData?.bank ?? null}
      bankBranch={meData?.bank_branch ?? null}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

export default AccountWithdrawalModal;
